/**
 * Notification Settings Page
 * 
 * Manage user notification preferences - what notifications to receive
 * and through which channels (email, push, in-app).
 */

import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faEnvelope,
  faMobileAlt,
  faDesktop,
  faMoon,
  faCheck,
  faTimes,
  faBox,
  faComment,
  faAt,
  faCopy,
  faChartLine,
  faShield,
  faCalendarWeek,
  faBullhorn,
  faSave,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { notificationSettingsAPI } from '../services/api';
import useI18n from '../hooks/useI18n';
import '../styles/NotificationSettings.css';

interface NotificationSettings {
  id?: string;
  // Notification types
  itemUpdates: boolean;
  comments: boolean;
  mentions: boolean;
  duplicateDetection: boolean;
  valuePredictions: boolean;
  securityAlerts: boolean;
  weeklyDigest: boolean;
  marketingEmails: boolean;
  // Channels
  emailNotifications: boolean;
  pushNotifications: boolean;
  inAppNotifications: boolean;
  // Per-notification email delivery
  emailItemUpdates: boolean;
  emailComments: boolean;
  emailMentions: boolean;
  emailDuplicateAlerts: boolean;
  emailValueChanges: boolean;
  emailSecurityAlerts: boolean;
  // Quiet hours
  quietHoursEnabled: boolean;
  quietHoursStart?: string;
  quietHoursEnd?: string;
}

const defaultSettings: NotificationSettings = {
  itemUpdates: true,
  comments: true,
  mentions: true,
  duplicateDetection: true,
  valuePredictions: true,
  securityAlerts: true,
  weeklyDigest: false,
  marketingEmails: false,
  emailNotifications: true,
  pushNotifications: true,
  inAppNotifications: true,
  emailItemUpdates: true,
  emailComments: true,
  emailMentions: true,
  emailDuplicateAlerts: false,
  emailValueChanges: false,
  emailSecurityAlerts: true,
  quietHoursEnabled: false,
  quietHoursStart: '22:00',
  quietHoursEnd: '08:00',
};

const mapBackendToUI = (data: any): NotificationSettings => ({
  ...defaultSettings,
  itemUpdates: data.itemUpdates ?? defaultSettings.itemUpdates,
  comments: data.comments ?? defaultSettings.comments,
  mentions: data.mentions ?? defaultSettings.mentions,
  duplicateDetection: data.duplicateAlerts ?? data.duplicateDetection ?? defaultSettings.duplicateDetection,
  valuePredictions: data.valueChanges ?? data.valuePredictions ?? defaultSettings.valuePredictions,
  securityAlerts: data.securityAlerts ?? defaultSettings.securityAlerts,
  weeklyDigest: data.weeklyDigest ?? defaultSettings.weeklyDigest,
  marketingEmails: data.marketingEmails ?? defaultSettings.marketingEmails,
  emailNotifications: data.emailEnabled ?? data.emailNotifications ?? defaultSettings.emailNotifications,
  pushNotifications: data.pushEnabled ?? data.pushNotifications ?? defaultSettings.pushNotifications,
  inAppNotifications: data.inAppEnabled ?? data.inAppNotifications ?? defaultSettings.inAppNotifications,
  emailItemUpdates: data.emailItemUpdates ?? defaultSettings.emailItemUpdates,
  emailComments: data.emailComments ?? defaultSettings.emailComments,
  emailMentions: data.emailMentions ?? defaultSettings.emailMentions,
  emailDuplicateAlerts: data.emailDuplicateAlerts ?? defaultSettings.emailDuplicateAlerts,
  emailValueChanges: data.emailValueChanges ?? defaultSettings.emailValueChanges,
  emailSecurityAlerts: data.emailSecurityAlerts ?? defaultSettings.emailSecurityAlerts,
  quietHoursEnabled: data.quietHoursEnabled ?? defaultSettings.quietHoursEnabled,
  quietHoursStart: data.quietHoursStart ?? defaultSettings.quietHoursStart,
  quietHoursEnd: data.quietHoursEnd ?? defaultSettings.quietHoursEnd,
});

const mapUIToBackend = (settings: NotificationSettings) => ({
  itemUpdates: settings.itemUpdates,
  comments: settings.comments,
  mentions: settings.mentions,
  duplicateAlerts: settings.duplicateDetection,
  valueChanges: settings.valuePredictions,
  securityAlerts: settings.securityAlerts,
  weeklyDigest: settings.weeklyDigest,
  marketingEmails: settings.marketingEmails,
  emailEnabled: settings.emailNotifications,
  pushEnabled: settings.pushNotifications,
  inAppEnabled: settings.inAppNotifications,
  emailItemUpdates: settings.emailItemUpdates,
  emailComments: settings.emailComments,
  emailMentions: settings.emailMentions,
  emailDuplicateAlerts: settings.emailDuplicateAlerts,
  emailValueChanges: settings.emailValueChanges,
  emailSecurityAlerts: settings.emailSecurityAlerts,
  quietHoursEnabled: settings.quietHoursEnabled,
  quietHoursStart: settings.quietHoursStart,
  quietHoursEnd: settings.quietHoursEnd,
});

const NotificationSettingsPage = () => {
  const [settings, setSettings] = useState<NotificationSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const { t } = useI18n();

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await notificationSettingsAPI.get();
      if (response.data) {
        setSettings(mapBackendToUI(response.data));
      }
    } catch (err: any) {
      // If no settings exist yet, use defaults
      console.log('Using default notification settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key: keyof NotificationSettings) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
    setHasChanges(true);
  };

  const handleTimeChange = (key: 'quietHoursStart' | 'quietHoursEnd', value: string) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await notificationSettingsAPI.update(mapUIToBackend(settings));
      setSuccess(t('notifSettings.saved'));
      setHasChanges(false);
    } catch (err: any) {
      setError(err.response?.data?.error || t('notifSettings.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleEnableAll = () => {
    setSettings((prev) => ({
      ...prev,
      itemUpdates: true,
      comments: true,
      mentions: true,
      duplicateDetection: true,
      valuePredictions: true,
      securityAlerts: true,
    }));
    setHasChanges(true);
  };

  const handleDisableAll = () => {
    setSettings((prev) => ({
      ...prev,
      itemUpdates: false,
      comments: false,
      mentions: false,
      duplicateDetection: false,
      valuePredictions: false,
      weeklyDigest: false,
      marketingEmails: false,
    }));
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="notification-settings-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('notifSettings.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="notification-settings-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>
            <FontAwesomeIcon icon={faBell} /> {t('notifSettings.title')}
          </h1>
          <p>{t('notifSettings.subtitle')}</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={handleDisableAll}>
            {t('notifSettings.disableAll')}
          </button>
          <button className="btn-secondary" onClick={handleEnableAll}>
            {t('notifSettings.enableAll')}
          </button>
          <button 
            className="btn-primary" 
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> {t('notifSettings.saving')}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} /> {t('notifSettings.saveChanges')}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="alert alert-error">
          <FontAwesomeIcon icon={faTimes} />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success">
          <FontAwesomeIcon icon={faCheck} />
          <span>{success}</span>
        </div>
      )}

      <div className="settings-content">
        {/* Notification Types */}
        <section className="settings-section">
          <div className="section-header">
            <h2><FontAwesomeIcon icon={faBell} /> {t('notifSettings.typesTitle')}</h2>
            <p>{t('notifSettings.typesDesc')} <FontAwesomeIcon icon={faEnvelope} style={{ color: '#667eea', fontSize: 12 }} /></p>
          </div>
          
          <div className="settings-list">
            <div className="setting-item">
              <div className="setting-icon">
                <FontAwesomeIcon icon={faBox} />
              </div>
              <div className="setting-info">
                <div className="setting-name">{t('notifSettings.itemUpdates')}</div>
                <div className="setting-description">
                  {t('notifSettings.itemUpdatesDesc')}
                </div>
              </div>
              <div className="setting-actions">
                <button
                  className={`email-toggle-btn${settings.emailItemUpdates && settings.itemUpdates ? ' active' : ''}`}
                  title={t('notifSettings.emailToggleTitle')}
                  disabled={!settings.itemUpdates}
                  onClick={() => handleToggle('emailItemUpdates')}
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                </button>
                <div className={`toggle${settings.itemUpdates ? ' checked' : ''}`} role="checkbox" aria-checked={settings.itemUpdates} tabIndex={0} onClick={() => handleToggle('itemUpdates')} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle('itemUpdates'); } }}><span className="toggle-slider"></span></div>
              </div>
            </div>

            <div className="setting-item">
              <div className="setting-icon">
                <FontAwesomeIcon icon={faComment} />
              </div>
              <div className="setting-info">
                <div className="setting-name">{t('notifSettings.comments')}</div>
                <div className="setting-description">
                  {t('notifSettings.commentsDesc')}
                </div>
              </div>
              <div className="setting-actions">
                <button
                  className={`email-toggle-btn${settings.emailComments && settings.comments ? ' active' : ''}`}
                  title={t('notifSettings.emailToggleTitle')}
                  disabled={!settings.comments}
                  onClick={() => handleToggle('emailComments')}
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                </button>
                <div className={`toggle${settings.comments ? ' checked' : ''}`} role="checkbox" aria-checked={settings.comments} tabIndex={0} onClick={() => handleToggle('comments')} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle('comments'); } }}><span className="toggle-slider"></span></div>
              </div>
            </div>

            <div className="setting-item">
              <div className="setting-icon">
                <FontAwesomeIcon icon={faAt} />
              </div>
              <div className="setting-info">
                <div className="setting-name">{t('notifSettings.mentions')}</div>
                <div className="setting-description">
                  {t('notifSettings.mentionsDesc')}
                </div>
              </div>
              <div className="setting-actions">
                <button
                  className={`email-toggle-btn${settings.emailMentions && settings.mentions ? ' active' : ''}`}
                  title={t('notifSettings.emailToggleTitle')}
                  disabled={!settings.mentions}
                  onClick={() => handleToggle('emailMentions')}
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                </button>
                <div className={`toggle${settings.mentions ? ' checked' : ''}`} role="checkbox" aria-checked={settings.mentions} tabIndex={0} onClick={() => handleToggle('mentions')} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle('mentions'); } }}><span className="toggle-slider"></span></div>
              </div>
            </div>

            <div className="setting-item">
              <div className="setting-icon">
                <FontAwesomeIcon icon={faCopy} />
              </div>
              <div className="setting-info">
                <div className="setting-name">{t('notifSettings.duplicateDetection')}</div>
                <div className="setting-description">
                  {t('notifSettings.duplicateDetectionDesc')}
                </div>
              </div>
              <div className="setting-actions">
                <button
                  className={`email-toggle-btn${settings.emailDuplicateAlerts && settings.duplicateDetection ? ' active' : ''}`}
                  title={t('notifSettings.emailToggleTitle')}
                  disabled={!settings.duplicateDetection}
                  onClick={() => handleToggle('emailDuplicateAlerts')}
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                </button>
                <div className={`toggle${settings.duplicateDetection ? ' checked' : ''}`} role="checkbox" aria-checked={settings.duplicateDetection} tabIndex={0} onClick={() => handleToggle('duplicateDetection')} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle('duplicateDetection'); } }}><span className="toggle-slider"></span></div>
              </div>
            </div>

            <div className="setting-item">
              <div className="setting-icon">
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              <div className="setting-info">
                <div className="setting-name">{t('notifSettings.valuePredictions')}</div>
                <div className="setting-description">
                  {t('notifSettings.valuePredictionsDesc')}
                </div>
              </div>
              <div className="setting-actions">
                <button
                  className={`email-toggle-btn${settings.emailValueChanges && settings.valuePredictions ? ' active' : ''}`}
                  title={t('notifSettings.emailToggleTitle')}
                  disabled={!settings.valuePredictions}
                  onClick={() => handleToggle('emailValueChanges')}
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                </button>
                <div className={`toggle${settings.valuePredictions ? ' checked' : ''}`} role="checkbox" aria-checked={settings.valuePredictions} tabIndex={0} onClick={() => handleToggle('valuePredictions')} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle('valuePredictions'); } }}><span className="toggle-slider"></span></div>
              </div>
            </div>

            <div className="setting-item">
              <div className="setting-icon security">
                <FontAwesomeIcon icon={faShield} />
              </div>
              <div className="setting-info">
                <div className="setting-name">{t('notifSettings.securityAlerts')}</div>
                <div className="setting-description">
                  {t('notifSettings.securityAlertsDesc')}
                </div>
              </div>
              <div className="setting-actions">
                <button
                  className={`email-toggle-btn${settings.emailSecurityAlerts && settings.securityAlerts ? ' active' : ''}`}
                  title={t('notifSettings.emailToggleTitle')}
                  disabled={!settings.securityAlerts}
                  onClick={() => handleToggle('emailSecurityAlerts')}
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                </button>
                <div className={`toggle${settings.securityAlerts ? ' checked' : ''}`} role="checkbox" aria-checked={settings.securityAlerts} tabIndex={0} onClick={() => handleToggle('securityAlerts')} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle('securityAlerts'); } }}><span className="toggle-slider"></span></div>
              </div>
            </div>
          </div>
        </section>

        {/* Email Preferences */}
        <section className="settings-section">
          <div className="section-header">
            <h2><FontAwesomeIcon icon={faEnvelope} /> {t('notifSettings.emailPrefs')}</h2>
            <p>{t('notifSettings.emailPrefsDesc')}</p>
          </div>
          
          <div className="settings-list">
            <div className="setting-item">
              <div className="setting-icon">
                <FontAwesomeIcon icon={faCalendarWeek} />
              </div>
              <div className="setting-info">
                <div className="setting-name">{t('notifSettings.weeklyDigest')}</div>
                <div className="setting-description">
                  {t('notifSettings.weeklyDigestDesc')}
                </div>
              </div>
              <div className={`toggle${settings.weeklyDigest ? ' checked' : ''}`} role="checkbox" aria-checked={settings.weeklyDigest} tabIndex={0} onClick={() => handleToggle('weeklyDigest')} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle('weeklyDigest'); } }}><span className="toggle-slider"></span></div>
            </div>

            <div className="setting-item">
              <div className="setting-icon">
                <FontAwesomeIcon icon={faBullhorn} />
              </div>
              <div className="setting-info">
                <div className="setting-name">{t('notifSettings.marketingEmails')}</div>
                <div className="setting-description">
                  {t('notifSettings.marketingEmailsDesc')}
                </div>
              </div>
              <div className={`toggle${settings.marketingEmails ? ' checked' : ''}`} role="checkbox" aria-checked={settings.marketingEmails} tabIndex={0} onClick={() => handleToggle('marketingEmails')} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle('marketingEmails'); } }}><span className="toggle-slider"></span></div>
            </div>
          </div>
        </section>

        {/* Delivery Channels */}
        <section className="settings-section">
          <div className="section-header">
            <h2><FontAwesomeIcon icon={faMobileAlt} /> {t('notifSettings.channels')}</h2>
            <p>{t('notifSettings.channelsDesc')}</p>
          </div>
          
          <div className="settings-list channels-list">
            <div className="channel-item">
              <div className="channel-icon email">
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
              <div className="channel-info">
                <div className="channel-name">{t('notifSettings.email')}</div>
                <div className="channel-description">{t('notifSettings.emailDesc')}</div>
              </div>
              <div className={`toggle${settings.emailNotifications ? ' checked' : ''}`} role="checkbox" aria-checked={settings.emailNotifications} tabIndex={0} onClick={() => handleToggle('emailNotifications')} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle('emailNotifications'); } }}><span className="toggle-slider"></span></div>
            </div>

            <div className="channel-item">
              <div className="channel-icon push">
                <FontAwesomeIcon icon={faMobileAlt} />
              </div>
              <div className="channel-info">
                <div className="channel-name">{t('notifSettings.push')}</div>
                <div className="channel-description">{t('notifSettings.pushDesc')}</div>
              </div>
              <div className={`toggle${settings.pushNotifications ? ' checked' : ''}`} role="checkbox" aria-checked={settings.pushNotifications} tabIndex={0} onClick={() => handleToggle('pushNotifications')} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle('pushNotifications'); } }}><span className="toggle-slider"></span></div>
            </div>

            <div className="channel-item">
              <div className="channel-icon in-app">
                <FontAwesomeIcon icon={faDesktop} />
              </div>
              <div className="channel-info">
                <div className="channel-name">{t('notifSettings.inApp')}</div>
                <div className="channel-description">{t('notifSettings.inAppDesc')}</div>
              </div>
              <div className={`toggle${settings.inAppNotifications ? ' checked' : ''}`} role="checkbox" aria-checked={settings.inAppNotifications} tabIndex={0} onClick={() => handleToggle('inAppNotifications')} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle('inAppNotifications'); } }}><span className="toggle-slider"></span></div>
            </div>
          </div>
        </section>

        {/* Quiet Hours */}
        <section className="settings-section">
          <div className="section-header">
            <h2><FontAwesomeIcon icon={faMoon} /> {t('notifSettings.quietHours')}</h2>
            <p>{t('notifSettings.quietHoursDesc')}</p>
          </div>
          
          <div className="quiet-hours-content">
            <div className="setting-item">
              <div className="setting-icon">
                <FontAwesomeIcon icon={faMoon} />
              </div>
              <div className="setting-info">
                <div className="setting-name">{t('notifSettings.enableQuietHours')}</div>
                <div className="setting-description">
                  {t('notifSettings.quietHoursSilenced')}
                </div>
              </div>
              <div className={`toggle${settings.quietHoursEnabled ? ' checked' : ''}`} role="checkbox" aria-checked={settings.quietHoursEnabled} tabIndex={0} onClick={() => handleToggle('quietHoursEnabled')} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle('quietHoursEnabled'); } }}><span className="toggle-slider"></span></div>
            </div>

            {settings.quietHoursEnabled && (
              <div className="time-range">
                <div className="time-input">
                  <label>{t('notifSettings.startTime')}</label>
                  <input
                    type="time"
                    value={settings.quietHoursStart || '22:00'}
                    onChange={(e) => handleTimeChange('quietHoursStart', e.target.value)}
                  />
                </div>
                <span className="time-separator">{t('notifSettings.timeTo')}</span>
                <div className="time-input">
                  <label>{t('notifSettings.endTime')}</label>
                  <input
                    type="time"
                    value={settings.quietHoursEnd || '08:00'}
                    onChange={(e) => handleTimeChange('quietHoursEnd', e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Floating Save Button */}
      <div className={`floating-save${hasChanges ? ' visible' : ''}`}>
        <span>{t('notifSettings.unsavedChanges')}</span>
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? t('notifSettings.saving') : t('notifSettings.saveChanges')}
        </button>
      </div>
    </div>
  );
};

export default NotificationSettingsPage;
